import { createSelector } from 'reselect';

import {
  getActiveField,
  getActiveFieldUuid,
  getActiveProductYear,
  getActiveScript,
} from 'current/selectors';
import { getExportParams, getRawScriptList } from 'export-selector/selectors';
import { isPrimaryScript, scriptIsOfExportScriptType } from 'scripts/selectors';

import { mycroftAffiliateIds } from 'constants/affiliates';
import { exportScopeTypes } from 'constants/exportSelector';
import { Affiliates, WorkPlan, Field, Script, Store } from 'store/types/types';

export const hasAffiliateById = (state: Store, affiliateId: string) => {
  return state.affiliates.connections.includes(
    mycroftAffiliateIds[affiliateId],
  );
};
export const getAffiliateById = (state: Store, affiliateId: string): Affiliates => {
  return hasAffiliateById(state, affiliateId)
    ? state.affiliates[mycroftAffiliateIds[affiliateId]]
    : {};
};
export const getAffiliateList = (state: Store) => state.affiliates;
export const getJohnDeereFields = createSelector(
  [
    (state: Store) => state.affiliates?.deere?.fields?.byId,
    (state: Store) => state.affiliates?.deere?.fields?.ids,
  ],
  (fields, ids = []) => ids.map((id) => fields[id]),
);
export const getJohnDeereClients = createSelector(
  [
    (state: Store) => state.affiliates?.deere?.clients?.byName,
    (state: Store) => state.affiliates?.deere?.clients?.ids,
  ],
  (clients, ids = []) => ids.map((id) => clients[id]),
);
export const getJohnDeereFieldsByClientName = createSelector(
  [
    (state: Store) => state.affiliates?.deere?.fields?.byId,
    (state: Store, clientName: string) =>
      state.affiliates?.deere?.fields?.byClient[clientName],
  ],
  (fields, ids = []) => ids.map((id) => fields[id]),
);
export const getJohnDeereWorkPlansForActiveField = createSelector(
  [
    (state: Store) => getActiveFieldUuid(state) as WorkPlan,
    (state: Store) => state.affiliates?.deere?.workPlans,
  ],
  (activeFieldUuid, workPlans = {}) =>
    Object.values(workPlans).filter(
      ({ fieldUuid }) => fieldUuid === activeFieldUuid,
    ),
);

/**
 * Returns a list of FV fields that can be used to create Work Plans based on
 * the flow followed by the user (single script, fields, or operation scripts)
 * and the type and year of the scripts for that flow. Fields that have scripts
 * of the selected type and year and belong to the selected operation will be returned.
 */
export const getFieldsForWorkPlanCreation = createSelector(
  [
    (state: Store) => getExportParams(state),
    (state: Store) => getActiveField(state) as Field,
    (state: Store) => getActiveProductYear(state) as string,
    (state: Store) => getRawScriptList(state),
    (state: Store) => state.fields.byUuid,
  ],
  (
    exportParams,
    activeField,
    activeYear,
    rawScriptList,
    fieldsByUuid
  ) => {
    if (exportParams?.scopeType !== exportScopeTypes.OPERATION) {
      return [activeField];
    }

    const fieldsUuids = rawScriptList
    .filter((script) => {
      return (
        script['cropping-year'] === activeYear
           && scriptIsOfExportScriptType(script, exportParams.scriptType)
      );
    })
    .map((script) => script['field-uuid']);

    return [...new Set(fieldsUuids)]
    .map((fieldUuid) => fieldsByUuid[fieldUuid])
    .filter((field): field is Field => !!field && field.operationId === exportParams.operationUuid)
    .sort((fieldA, fieldB) => fieldA.name.localeCompare(fieldB.name));
  }
);


/**
 * Returns the list of uuids of the fields selected to be used in the creation of Work Plans.
 * For the operation flow, these will be the fields selected by the user
 * and belonging to the active operation. For the single field flow, it will be the active field.
 */
export const getSelectedFvFieldsUuidsForWorkPlans = createSelector(
  [
    (state: Store) => state.exportSelector.isFvFieldSelected,
    (state: Store) => getExportParams(state),
    (state: Store) => getActiveFieldUuid(state) as WorkPlan,
    (state: Store) => state.fields.byUuid,
  ],
  (selectedFvFields, exportParams, activeFieldUuid, fields) => {
    if (exportParams?.scopeType === exportScopeTypes.OPERATION) {
      return Object.entries(selectedFvFields).flatMap(([uuid, selected]) =>
        selected && fields[uuid].operationId === exportParams.operationUuid
          ? uuid
          : [],
      );
    }

    return [activeFieldUuid];
  },
);

/**
 * Returns the list of scripts to be used to create Work Plans, including the needed John Deere
 * data (JD field id, and organization id).
 * The scripts are filtered base on: its product year, its activity type (planting, fertility, etc.),
 * if they belong to the selected fields by the user, if they are primary or not depending on user selection.
 */
export const getScriptsForWorkPlansCreation = createSelector(
  [
    (state: Store) => getRawScriptList(state),
    (state: Store) => getActiveProductYear(state) as string,
    (state: Store) => getExportParams(state),
    (state: Store) => getActiveScript(state) as Script,
    (state: Store) => getSelectedFvFieldsUuidsForWorkPlans(state),
    (state: Store) => state.affiliates.deere.fields.byId,
  ],
  (
    rawScriptList,
    activeYear,
    exportParams,
    activeScript,
    selectedFvFieldsUuids,
    jdFields,
  ) => {
    const filteredScripts = exportParams?.scopeType
      ? rawScriptList.filter((script) => {
        return (
          selectedFvFieldsUuids.includes(script['field-uuid'])
            && script['cropping-year'] === activeYear
            && scriptIsOfExportScriptType(script, exportParams.scriptType)
            && (exportParams.primaryOnly ? isPrimaryScript(script) : true)
        );
      })
      : [activeScript];

    return filteredScripts.map((script) => {
      const { id, organizationId } = Object.values(jdFields).find(
        ({ fvFieldUuid }) => fvFieldUuid === script['field-uuid'],
      );

      return { ...script, jdFieldId: id, organizationId };
    });
  },
);
